// React
import { useEffect, useState } from "react"

// Types
import type { SuccessFetch } from "types"
import type { DataType, FetchType, Filter, FilterType } from "../../types"

// Utils
import { useApi } from "utils"

// Types
interface PaginationType {
  page: number
  pages: number
  size: number
  total: number
}
interface Type extends Filter {
  data: DataType[] 
  isLoading: boolean
  pagination: PaginationType
}

export function useList(): Type {
  // Vars
  const defaultFilter: FilterType = {
    buy: false,
    group_id: "",
    inventory: false,
    limit: 10,
    page: 1,
    search: "",
    sell: false
  }

  // Hooks
  const api = useApi()
  const [data, setData] = useState<DataType[]>([])
  const [isLoading, setLoading] = useState<boolean>(false)
  const [filter, setFilter] = useState<FilterType>(defaultFilter)
  const [pagination, setPagination] = useState<PaginationType>({
    page: 1,
    pages: 0,
    size: 0,
    total: 0
  })

  useEffect(() => {
    setLoading(true)

    api.get(
      "/list_build_item/list_build_item",
      {
        params: {
          ...filter,
          group_id: filter.group_id || undefined
        }
      }
    ).then((res: SuccessFetch<{
      payload: FetchType[]
      pagination_data: PaginationType
    }>) => {
      const formattedData = res.data.payload.map((item: FetchType) => ({
        ...item,
        cac: parseFloat(item.cac),
        price_retail: parseFloat(item.price_retail),
        avg_price: parseFloat(item.avg_price),
        soh: parseFloat(item.soh)
      }))

      setData(formattedData)
      setPagination(res.data.pagination_data)
    }).catch(() => {
      setData([])
    }).finally(() => {
      setLoading(false)
    })

    return () => {
      setData([])
      setLoading(false)
      setPagination({
        page: 1,
        pages: 0,
        size: 0,
        total: 0
      })
    }

    // eslint-disable-next-line
  }, [filter])

  const handleCheckbox = (field: "buy" | "inventory" | "sell"): void => {
    setFilter(prev => ({
      ...prev,
      [field]: !prev[field],
      page: 1
    }))
  }
  const handlePage = (page: number): void => {
    setFilter(prev => ({
      ...prev,
      page
    }))
  }
  const handlePerPage = (perPage: number): void => {
    setFilter(prev => ({
      ...prev,
      limit: perPage,
      page: 1
    }))
  }
  const handleSearchGroup = (
    field: "search" | "group_id",
    value: string
  ): void => {
    setFilter(prev => ({
      ...prev,
      [field]: value,
      page: 1
    }))
  }
  const reset = (): void => {
    setFilter(defaultFilter)
  }

  return {
    data,
    filter,
    isLoading,
    pagination,
    handleCheckbox,
    handlePage,
    handlePerPage,
    handleSearchGroup,
    reset
  }
}